import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowLeft,
  Check,
  Linkedin,
  Mail,
  Sparkles,
  Users,
  Terminal,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";

const LINKEDIN_URL = "https://www.linkedin.com/company/deploydnow";

export default function Welcome() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;
  const fromBenchmark = location.state?.from === "benchmark";

  // If user lands on /welcome directly (no email in state), allow page anyway
  // but show a friendlier neutral message. No redirect.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="welcome-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#F7F6F1] text-zinc-900"
    >
      {/* Background ambient gradients */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 18% 18%, rgba(22,163,74,0.14), transparent 42%), radial-gradient(circle at 85% 82%, rgba(22,163,74,0.08), transparent 45%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.45]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,10,10,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,0.06) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 80%)",
        }}
      />
      <div className="grain-overlay" />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between border-b border-zinc-200 px-6 py-4 sm:px-10 lg:px-16">
        <div className="flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500">
          <Terminal className="h-3.5 w-3.5 text-emerald-600" />
          <span data-testid="brand-mark">
            <BrandMark size="inline" /> / system
          </span>
        </div>
        <Link
          to="/"
          data-testid="back-home-link"
          className="group inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-600 transition-colors hover:text-emerald-700"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Back home
        </Link>
      </header>

      <section className="relative z-10 mx-auto w-full max-w-4xl px-6 pb-24 pt-20 sm:px-10 sm:pt-28">
        <div className="reveal flex flex-col">
          {/* Success pill */}
          <div
            data-testid="success-badge"
            className="mb-8 inline-flex w-fit items-center gap-3 border border-emerald-500/60 bg-emerald-500/10 px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-emerald-800 backdrop-blur"
          >
            <Check className="h-3.5 w-3.5 text-emerald-700" />
            {fromBenchmark ? "submission received · thank you" : "confirmed · added to the list"}
          </div>

          {/* Hero */}
          <h1
            data-testid="welcome-title"
            className="font-display text-[clamp(3rem,9vw,7rem)] font-extrabold leading-[0.92] tracking-tight text-zinc-900"
          >
            {fromBenchmark ? "Thanks for" : "Welcome aboard,"}
            <br />
            {fromBenchmark ? (
              <span className="text-emerald-700">contributing.</span>
            ) : (
              <BrandMark size="hero" pulsing className="font-display" />
            )}
          </h1>

          <p
            data-testid="welcome-tagline"
            className="mt-6 font-mono-tech text-sm uppercase tracking-[0.3em] text-emerald-600"
          >
            {fromBenchmark ? (
              <>You&apos;re a contributor<span className="caret">_</span></>
            ) : (
              <>You&apos;re in<span className="caret">_</span></>
            )}
          </p>

          {/* Receipt-style confirmation */}
          <div
            data-testid="confirmation-receipt"
            className="mt-10 max-w-2xl border border-zinc-300 bg-white p-6 shadow-[0_1px_0_0_rgba(10,10,10,0.04),0_24px_60px_-30px_rgba(10,10,10,0.18)] sm:p-7"
          >
            <div className="flex items-center gap-3 border-b border-zinc-200 pb-4 font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
              <Mail className="h-3.5 w-3.5 text-emerald-600" />
              <span>// confirmation</span>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Row label="status" value={fromBenchmark ? "submission recorded" : "queued for first update"} mono />
              <Row label="access" value="full benchmark · pre-public" mono />
              {email && (
                <Row label="inbox" value={email} className="sm:col-span-2" mono />
              )}
            </div>
            <p className="mt-6 font-display text-lg leading-snug text-zinc-800 sm:text-xl">
              We&apos;ll only email you when there&apos;s something{" "}
              <em className="not-italic font-bold text-zinc-900 underline decoration-emerald-500 decoration-2 underline-offset-4">
                actually
              </em>{" "}
              worth your time.
            </p>
          </div>

          {/* What happens next */}
          <h2
            data-testid="next-steps-title"
            className="mt-16 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-zinc-500"
          >
            // what happens next
          </h2>
          <div className="mt-5 grid max-w-3xl grid-cols-1 gap-px bg-zinc-200 sm:grid-cols-3">
            <Step
              icon={<Mail className="h-4 w-4" />}
              k="01"
              title="First update"
              body="The very first launch note lands in your inbox before anyone else."
            />
            <Step
              icon={<Users className="h-4 w-4" />}
              k="02"
              title="Peer network"
              body="See what your peers are actually shipping — no vendor hype."
            />
            <Step
              icon={<Sparkles className="h-4 w-4" />}
              k="03"
              title="Founders access"
              body="First 100 signups get early access when the platform opens."
            />
          </div>

          {/* CTAs */}
          <div className="mt-12 flex max-w-3xl flex-col items-start justify-between gap-5 border-t border-zinc-200 pt-8 sm:flex-row sm:items-center">
            <a
              data-testid="welcome-linkedin"
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 border border-zinc-900 bg-zinc-900 px-5 py-3 font-mono-tech text-[11px] uppercase tracking-[0.24em] text-white transition-colors hover:border-emerald-600 hover:bg-emerald-600"
            >
              <Linkedin className="h-4 w-4" />
              <span>Follow on LinkedIn</span>
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
            <button
              data-testid="welcome-share"
              type="button"
              onClick={() => {
                const url = window.location.origin;
                if (navigator.share) {
                  navigator.share({
                    title: "deployd — launching soon",
                    text: "See what actually works. Verified technology use cases from peers.",
                    url,
                  }).catch(() => {});
                } else if (navigator.clipboard) {
                  navigator.clipboard.writeText(url);
                }
              }}
              className="group inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.24em] text-zinc-700 transition-colors hover:text-emerald-700"
            >
              Tell a peer
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-zinc-200 px-6 py-6 sm:px-10 lg:px-16">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-start justify-between gap-3 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500 sm:flex-row sm:items-center">
          <span>
            © {new Date().getFullYear()} <BrandMark size="inline" />, Inc.
          </span>
          <span className="inline-flex items-center gap-2">
            <Check className="h-3 w-3 text-emerald-600" /> Built for technology
            leaders &middot; San Francisco
          </span>
        </div>
      </footer>
    </main>
  );
}

const Row = ({ label, value, className = "", mono = false }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <span className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
      {label}
    </span>
    <span
      className={`break-all text-base text-zinc-900 ${
        mono ? "font-mono-tech text-sm" : "font-display"
      }`}
    >
      {value}
    </span>
  </div>
);

const Step = ({ icon, k, title, body }) => (
  <div className="flex flex-col gap-3 bg-white p-6">
    <div className="flex items-center gap-3 text-emerald-700">
      <span className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-emerald-700">
        {k}
      </span>
      <span className="text-emerald-700">{icon}</span>
    </div>
    <h3 className="font-display text-lg font-semibold text-zinc-900">{title}</h3>
    <p className="text-sm leading-relaxed text-zinc-600">{body}</p>
  </div>
);
